import { Injectable } from '@angular/core';
import { CollectionFinderItemDTO } from '../../interfaces/collection.interface';
import { SearchCriteria } from 'src/app/core/interfaces/search-criteria.interface';
import { SearchableCollectionInterface } from './searchable-collection.interface';
import { FIELD_PRIORITY_WEIGHT, SEARCH_FIELD, SearchPriorityConfig } from '../../interfaces/search-priority-config';

@Injectable({
  providedIn: 'root',
})
export class CollectionsSearchService {
  search(
    _collections: CollectionFinderItemDTO[],
    _query: string,
    _criteria?: SearchCriteria[],
  ): CollectionFinderItemDTO[] {
    const normalizedQuery = this.normalizeQuery(_query);

    if (!normalizedQuery) return _collections;
    const searchQuery = this.getSearchQuery(normalizedQuery);

    if (normalizedQuery.startsWith('#')) {
      const keySearch = normalizedQuery.slice(1).toLowerCase();
      return _collections
        .map((_collection) =>
          this.calculateMatchScoreKeyWord(
            _collection as SearchableCollectionInterface,
            _collection.keywords ?? [],
            keySearch,
          ),
        )
        .filter((_collection) => _collection.totalMatchScore > 0)
        .sort(this.sortByTotalMatchScore)
    }

    if (_criteria) {
      return _collections
        .map((_collection) =>
          this.calculateMathScore(
            _collection as SearchableCollectionInterface,
            searchQuery,
            _criteria,
          ),
        )
        .filter((_collection) => _collection.totalMatchScore > 0)
    }

    return _collections.filter(
      (x) =>
        x.title.toLowerCase().indexOf(normalizedQuery.toLowerCase()) > -1 ||
        (x.subtitle || '').toLowerCase().indexOf(normalizedQuery.toLowerCase()) > -1,
    );
  }

  private normalizeQuery(_query: string): string {
    if (!_query) return '';
    let query = _query.trim();

    if (!query) return '';

    if (!query.startsWith('#')) return query;
    query = '#' + query.slice(1).replace(/\s+/g, '');

    return query === '#' ? '' : query;
  }

  private calculateMathScore(
    _collection: SearchableCollectionInterface,
    _searchQuery: string[],
    _criteria?: SearchCriteria[],
  ): SearchableCollectionInterface {
    let totalMatchScore: number = 0;

    let bestPriorityConfig: SearchPriorityConfig = {
      relevanceField: SEARCH_FIELD.OTHER,
      fieldPriorityWeight: FIELD_PRIORITY_WEIGHT.OTHER,
      matchScore: 0
    };

    for (const criteria of _criteria ?? []) {

      let currentCriteriaSearchPriorityConfig = {} as SearchPriorityConfig;

      let criteriaMatchScore = 0;

      for (const _queryPart of _searchQuery) {
        const queryPartMatch = criteria.getMatchScore(_collection, _queryPart);

        currentCriteriaSearchPriorityConfig = criteria.getRelevanceFieldWeightAndMatchScore(_collection, _queryPart);

        if (queryPartMatch === 0) {
          criteriaMatchScore = 0;
          break;
        } else {
          criteriaMatchScore = criteriaMatchScore + queryPartMatch;
        }
      }

      if(criteriaMatchScore > 0 && bestPriorityConfig.fieldPriorityWeight < currentCriteriaSearchPriorityConfig.fieldPriorityWeight) {
        bestPriorityConfig = currentCriteriaSearchPriorityConfig;
        bestPriorityConfig.matchScore = criteriaMatchScore;
      }

      totalMatchScore = totalMatchScore + criteriaMatchScore;
    }
    return { ..._collection, totalMatchScore, searchPriorityConfig: bestPriorityConfig };
  }

  public sortByCollectionSearchPriority(a: SearchableCollectionInterface, b: SearchableCollectionInterface): number {
    const aConfig = a.searchPriorityConfig;
    const bConfig = b.searchPriorityConfig;

    return (
      bConfig.fieldPriorityWeight - aConfig.fieldPriorityWeight ||
      bConfig.matchScore - aConfig.matchScore
    );
  }

  public sortByTotalMatchScore(a: SearchableCollectionInterface, b: SearchableCollectionInterface) {
    return b.totalMatchScore - a.totalMatchScore;
  }

  private getSearchQuery(_searchQuery: string): string[] {
    return _searchQuery
      .toLowerCase()
      .split(' ')
      .filter((t) => t.length > 0);
  }

  private calculateMatchScoreKeyWord(
    _collection: SearchableCollectionInterface,
    keywords: string[],
    query: string,
  ): SearchableCollectionInterface {
    const totalMatchScore = (keywords ?? []).reduce((score, keyword) => {
      const kw = keyword.toLowerCase();
      if (kw.startsWith(query)) {
        score += 2;
      } else if (kw.includes(query)) {
        score += 1;
      }
      return score;
    }, 0);
    return { ..._collection, totalMatchScore };
  }
}
